export interface JobListItem {
  id: number;
  opening_date: string;
  closing_date: string;
  description?: string;
  department?: { id: number; name: string };
  hiring_manager?: {
    id: number;
    name?: string; // Direct name field from API
    first_name?: string; // Fallback for old format
    middle_name?: string;
    last_name?: string;
    // Can be a file ID or null
    profile_picture?: number | null;
    profile_picture_url?: string | null;
  };
  job_title?: string;
  job_level?: { id: number; name: string };
  region?: { id: number; name: string };
  nationality?:
    | { id: number; name: string }
    | Array<{ id: number; name: string }>;
  management_skills?: Array<{
    management_skill?: { id: number; name: string };
    years_of_experience?: number;
  }>;
  manages_people?: boolean;
  people_managed_range?: { id: number; name: string } | string | number;
  travel_requirement?: string;
  city?: { id: number; name: string };
  country?: { id: number; name: string };
  employment_type?: Array<{ id: number | string; name: string }>;
  workplace_type?: Array<{ id: number | string; name: string }>;
  areas_of_expertise?: Array<{
    field_of_expertise?: { id: number; name: string };
    years_in_this_area_of_expertise?: number;
    skills_must_have?: Array<{ id: number; name: string }>;
    skills_nice_to_have?: Array<{ id: number; name: string }>;
  }>;
  education?: Array<{
    qualification?: { id: number; name: string };
    field_of_study?: { id: number; name: string };
  }>;
  language_skills?: Array<{
    language?: { id: number; name: string };
    speaking_level?: string;
    reading_writing_level?: string;
  }>;
  certificate?: Array<{ id: number; name: string }>;
  industry?: Array<{ id: number; name: string }>;
  driving_license?: string;
  visa_status?: string;
  gender?: string;
  status?: string;
  incoming_requests?: number;
  new_matches?: number;
  in_match?: number; // Direct in_match field from API
  shortlisted?: number;
  in_review?: number;
  not_selected?: number;
  top_match?: number;
  days_open?: number;
  created_at?: string;
  updated_at?: string;
}

export interface JobListFilters {
  status?: string;
  job_title?: string;
  department_ids?: number[];
  time_range_start?: string;
  time_range_end?: string;
  sort_by?: string;
  page?: number;
  page_size?: number;
  limit?: number;
  offset?: number;
}

export async function createJob(
  payload: any,
  accessToken: string
): Promise<{ data?: any; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/job/job/create/`;

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "POST",
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(payload),
  });

  const responseBody = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: (responseBody && responseBody.message) || 'Job creation failed.' };
  }
  return { data: responseBody };
}

export async function fetchJobSummary(
  jobText: string,
  accessToken: string
): Promise<{ summary?: string; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://jobapi.jobmeinportal.com";
  const endpoint = `${API_BASE_URL}/api/job/job/summary/`;

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "POST",
    headers: {
      accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ job_text: jobText }),
  });

  const responseBody = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: responseBody?.message || "Failed to generate job description." };
  }

  return { summary: responseBody?.data?.summary || "" };
}

export interface CertificateSeedItem {
  id: number;
  name: string;
}

export interface FieldOfStudySeedItem {
  id: number;
  name: string;
}

export async function createCertificateSeed(
  name: string,
  accessToken: string
): Promise<{ data?: CertificateSeedItem; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/candidate/certificate/create/`;

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "POST",
    headers: {
      accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ name }),
  });

  const responseBody = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: responseBody?.message || "Failed to create certificate." };
  }

  const certificateData = responseBody?.data || responseBody;
  return { data: certificateData as CertificateSeedItem };
}

export async function createFieldOfStudySeed(
  name: string,
  accessToken: string
): Promise<{ data?: FieldOfStudySeedItem; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/candidate/field-of-study/create/`;

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "POST",
    headers: {
      accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ name }),
  });

  const responseBody = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: responseBody?.message || "Failed to create field of study." };
  }

  const fieldOfStudyData = responseBody?.data || responseBody;
  return { data: fieldOfStudyData as FieldOfStudySeedItem };
}

export async function updateJob(
  jobId: number,
  payload: any,
  accessToken: string
): Promise<{ data?: any; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/job/job/${jobId}/`;

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "PUT",
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(payload),
  });

  const responseBody = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: (responseBody && responseBody.message) || 'Job update failed.' };
  }
  return { data: responseBody };
}

export async function updateJobStatus(
  jobId: number,
  status: "Open" | "Close" | "On-hold",
  accessToken: string
): Promise<{ data?: any; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/job/job/status/update/${jobId}/`;

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ status }),
  });

  const responseBody = await response.json().catch(() => null);
  if (!response.ok) {
    return {
      error: (responseBody && responseBody.message) || "Job status update failed.",
    };
  }
  return { data: responseBody };
}

export async function fetchJobDetail(
  jobId: number,
  accessToken: string
): Promise<{ data?: JobListItem; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/job/job/${jobId}/`;

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
  });

  const responseBody = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: (responseBody && responseBody.message) || 'Failed to fetch job details.' };
  }
  // API response wraps data in a 'data' property
  const jobData = responseBody?.data || responseBody;
  return { data: jobData as JobListItem };
}

export async function downloadJobFile(
  jobId: number,
  accessToken: string
): Promise<{ data?: { blob: Blob; filename: string }; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/job/job/${jobId}/download/`;

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "GET",
    headers: {
      accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const responseBody = await response.json().catch(() => null);
    return { error: responseBody?.message || "Failed to download job file." };
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get("content-disposition") || "";
  const filenameMatch =
    contentDisposition.match(/filename\*=UTF-8''([^;]+)/i) ||
    contentDisposition.match(/filename="?([^"]+)"?/i);
  const filename = filenameMatch?.[1]
    ? decodeURIComponent(filenameMatch[1])
    : `job-${jobId}`;

  return { data: { blob, filename } };
}

export async function fetchJobList(
  accessToken: string,
  filters?: JobListFilters
): Promise<{ data?: JobListItem[]; error?: string; pagination?: { count: number; total_pages: number; current_page: number } }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  let endpoint = `${API_BASE_URL}/api/job/job/list/`;
  const limit =
    filters?.limit ??
    filters?.page_size;
  const offset =
    filters?.offset ??
    (
      filters?.page && limit !== undefined
        ? (filters.page - 1) * limit
        : undefined
    );

  // Build query parameters
  const queryParams: string[] = [];
  
  if (filters?.status && filters.status !== "All jobs") {
    queryParams.push(`status=${encodeURIComponent(filters.status)}`);
  }
  
  if (filters?.job_title?.trim()) {
    queryParams.push(`job_title=${encodeURIComponent(filters.job_title.trim())}`);
  }

  if (filters?.department_ids && filters.department_ids.length > 0) {
    filters.department_ids.forEach(id => {
      queryParams.push(`department_ids=${id}`);
    });
  }
  
  if (filters?.time_range_start) {
    queryParams.push(`time_range_start=${encodeURIComponent(filters.time_range_start)}`);
  }
  
  if (filters?.time_range_end) {
    queryParams.push(`time_range_end=${encodeURIComponent(filters.time_range_end)}`);
  }
  
  if (filters?.sort_by) {
    queryParams.push(`sort_by=${encodeURIComponent(filters.sort_by)}`);
  }

  if (limit !== undefined) {
    queryParams.push(`limit=${limit}`);
  }

  if (offset !== undefined) {
    queryParams.push(`offset=${offset}`);
  }

  if (queryParams.length > 0) {
    endpoint += `?${queryParams.join("&")}`;
  }

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
  });

  const responseBody = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: (responseBody && responseBody.message) || 'Failed to fetch job list.' };
  }
  
  // Handle different response structures
  let jobsData: JobListItem[] = [];
  let paginationCount: number | undefined;

  if (responseBody) {
    // Check if response has results array (pagination format)
    if (Array.isArray(responseBody.results)) {
      jobsData = responseBody.results as JobListItem[];
      paginationCount = responseBody.pagination?.count;
    } 
    // Check if response has data array
    else if (Array.isArray(responseBody.data)) {
      jobsData = responseBody.data as JobListItem[];
      paginationCount = responseBody.pagination?.count;
    }
    // Check if response is directly an array
    else if (Array.isArray(responseBody)) {
      jobsData = responseBody as JobListItem[];
    }
    // Check if response has nested data structure
    else if (responseBody.data && Array.isArray(responseBody.data)) {
      jobsData = responseBody.data as JobListItem[];
      paginationCount = responseBody.pagination?.count;
    }
  }

  const count = paginationCount ?? jobsData.length;
  const resolvedLimit = limit && limit > 0 ? limit : jobsData.length || 1;
  const currentPage = Math.floor((offset ?? 0) / resolvedLimit) + 1;
  const pagination = {
    count,
    total_pages: Math.max(1, Math.ceil(count / resolvedLimit)),
    current_page: currentPage,
  };
  
  return { 
    data: jobsData,
    pagination
  };
}

export async function runJobMatches(
  jobId: number,
  accessToken: string
): Promise<{ data?: { message: string; matches_created: number; matches_updated: number }; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/matching/jobs/${jobId}/run-matches/`;

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "POST",
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
  });

  const responseBody = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: (responseBody && responseBody.message) || 'Failed to run job matches.' };
  }
  const matchData = responseBody?.data || responseBody;
  return { data: matchData };
}

export interface JobHardFiltersPayload {
  employment_type: boolean;
  workplace_type: boolean;
  city: boolean;
  qualification: boolean;
  language: boolean;
  visa_status: boolean;
  driving_license: boolean;
  nationality: boolean;
}

export function buildJobHardFiltersPayload(input: {
  selectedEmploymentTypeId: string | null;
  selectedWorkplaceTypeId: string | null;
  selectedCityId: number | null;
  educationRequirements: Array<{
    qualificationId: number | null;
    fieldOfStudyId: number | null;
    fieldOfStudyOtherName: string | null;
  }>;
  languageRequirements: Array<{
    languageId: number | null;
    speakingLevelId: string | null;
    readingWritingLevelId: string | null;
  }>;
  visaStatusValue: string | null;
  drivingLicenseValue: string | null;
  selectedNationalityId: number | null;
}): JobHardFiltersPayload {
  return {
    employment_type: Boolean(input.selectedEmploymentTypeId),
    workplace_type: Boolean(input.selectedWorkplaceTypeId),
    city: Boolean(input.selectedCityId),
    qualification: input.educationRequirements.some((req) => req.qualificationId !== null),
    language: input.languageRequirements.some((req) => req.languageId !== null),
    visa_status: Boolean(input.visaStatusValue),
    driving_license: Boolean(input.drivingLicenseValue),
    nationality: Boolean(input.selectedNationalityId),
  };
}

export function hasAnyJobHardFilterSelected(payload: JobHardFiltersPayload): boolean {
  return Object.values(payload).some(Boolean);
}

export async function applyJobHardFilters(
  jobId: number,
  payload: JobHardFiltersPayload,
  accessToken: string
): Promise<{ data?: unknown; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/matching/jobs/${jobId}/hard-filters/`;

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "POST",
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(payload),
  });

  const responseBody = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: (responseBody && responseBody.message) || 'Failed to apply job hard filters.' };
  }
  return { data: responseBody?.data ?? responseBody };
}

export interface JobMatch {
  id: number;
  overall_score: string;
  raw?: RecruiterMatchItem;
  candidate: {
    id: number;
    profile_picture: number | null;
    profile_picture_url?: string | null;
    name: string;
    first_field_of_expertise?: {
      id: number;
      name: string;
    };
    years_of_experience?: {
      id: number;
      name: string;
    };
    first_qualification?: {
      id: number;
      name: string;
    };
    first_field_of_study?: {
      id: number;
      name: string;
    };
    last_job_position?: string;
    last_company?: string;
  };
  candidate_action: string | null;
  candidate_action_date: string | null;
  candidate_action_time: string | null;
  recruiter_action: string | null;
  recruiter_action_date: string | null;
  recruiter_action_time: string | null;
  feedback: number;
  /** Match stage from API: "Incoming request" | "In review" | "Shortlisted" | null for initial */
  stage?: string | null;
}

export interface JobMatchFilters {
  match_stage?: string;
  time_range_start?: string;
  time_range_end?: string;
  limit?: number;
  offset?: number;
  match_type?: "normal" | "extended";
}

export interface RecruiterMatchExpertiseArea {
  id: number;
  field_of_expertise: string;
  companies?: string[];
}

export interface RecruiterMatchItem {
  [key: string]: any;
  id: number;
  candidate_id: number;
  candidate_name: string;
  last_job_position?: string | null;
  areas_of_expertise?: RecruiterMatchExpertiseArea[];
  candidate_profile_picture: number | null;
  candidate_profile_picture_url?: string | null;
  match_score: string;
  applied_ago: string | null;
  applied_date: string | null;
  status: string;
  interview_date: string | null;
  overall_feedback_rating: number;
  stage?: string | null;
}

function getLastCompanyFromMatchItem(item: RecruiterMatchItem): string | undefined {
  const firstCompany = item.areas_of_expertise?.[0]?.companies?.[0];
  return typeof firstCompany === "string" && firstCompany.trim() ? firstCompany.trim() : undefined;
}

function mapRecruiterMatchToJobMatch(item: RecruiterMatchItem): JobMatch {
  const statusLower = (item.status || "").toLowerCase().replace(/\s+/g, "_");
  const datePart = (d: string | null) => (d ? d.split("T")[0] : null);
  const timePart = (d: string | null) =>
    d && d.includes("T") ? d.split("T")[1]?.slice(0, 8) ?? "00:00:00" : "00:00:00";
  return {
    id: item.id,
    overall_score: item.match_score,
    raw: item,
    candidate: {
      id: item.candidate_id,
      name: item.candidate_name,
      profile_picture: item.candidate_profile_picture,
      profile_picture_url: item.candidate_profile_picture_url ?? null,
      first_field_of_expertise: undefined,
      years_of_experience: undefined,
      last_job_position: item.last_job_position?.trim() || undefined,
      last_company: getLastCompanyFromMatchItem(item),
    },
    candidate_action: statusLower === "new" ? null : statusLower,
    candidate_action_date: datePart(item.applied_date),
    candidate_action_time: item.applied_date ? timePart(item.applied_date) : null,
    recruiter_action: item.status || null,
    recruiter_action_date: datePart(item.interview_date),
    recruiter_action_time: item.interview_date ? timePart(item.interview_date) : null,
    feedback: item.overall_feedback_rating ?? 0,
    stage: item.stage ?? null,
  };
}

export async function fetchJobMatches(
  jobId: number,
  accessToken: string,
  filters?: JobMatchFilters
): Promise<{ data?: JobMatch[]; error?: string; pagination?: { count: number } }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const queryParams: string[] = [];
  if (filters?.match_stage) {
    queryParams.push(`match_stage=${encodeURIComponent(filters.match_stage)}`);
  }
  queryParams.push(`match_type=${encodeURIComponent(filters?.match_type || "normal")}`);
  if (filters?.time_range_start) {
    queryParams.push(`time_range_start=${encodeURIComponent(filters.time_range_start)}`);
  }
  if (filters?.time_range_end) {
    queryParams.push(`time_range_end=${encodeURIComponent(filters.time_range_end)}`);
  }
  if (filters?.limit !== undefined) {
    queryParams.push(`limit=${filters.limit}`);
  }
  if (filters?.offset !== undefined) {
    queryParams.push(`offset=${filters.offset}`);
  }
  const endpoint = `${API_BASE_URL}/api/matching/recruiter/matches/${jobId}/?${queryParams.join("&")}`;

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "GET",
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  const responseBody = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: (responseBody && responseBody.message) || "Failed to fetch job matches." };
  }
  const rawData: RecruiterMatchItem[] = Array.isArray(responseBody?.data) ? responseBody.data : [];
  const data = rawData.map(mapRecruiterMatchToJobMatch);
  return {
    data,
    pagination: responseBody?.pagination,
  };
}

export interface MatchActionPayload {
  action: "interested" | "rejected";
}

// Action Apiss /api/matching/recruiter/matches/{match_id}/action/ 
export interface RecruiterMatchActionPayload {
  stage: string;
  status: string;
  interview_date?: string;
}

export async function updateRecruiterMatchAction(
  matchId: number,
  payload: RecruiterMatchActionPayload,
  accessToken: string
): Promise<{ success: boolean; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/matching/recruiter/matches/${matchId}/action/`;

  try {
    const { authenticatedFetch } = await import("../auth/apiInterceptor");
    const body: Record<string, string> = { stage: payload.stage, status: payload.status };
    if (payload.interview_date) body.interview_date = payload.interview_date;

    const response = await authenticatedFetch(endpoint, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(body),
    });

    const responseBody = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        error: responseBody?.message || "Failed to update match action.",
      };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Failed to update match action.",
    };
  }
}

export async function updateMatchAction(
  jobId: number,
  matchId: number,
  payload: MatchActionPayload,
  accessToken: string
): Promise<{ success: boolean; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/matching/jobs/${jobId}/matches/${matchId}/action/`;

  try {
    const { authenticatedFetch } = await import("../auth/apiInterceptor");
    const response = await authenticatedFetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    });

    const responseBody = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        error: responseBody?.message || "Failed to update match action.",
      };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Failed to update match action.",
    };
  }
}

type CandidateLocationRef = {
  id: number;
  name: string;
};

export function formatCandidateCurrentLocation(
  currentLocation?: {
    id?: number;
    name?: string;
    city?: CandidateLocationRef | null;
    country?: CandidateLocationRef | null;
  } | null
): string {
  if (!currentLocation) return "N/A";

  const cityName =
    currentLocation.city?.name ??
    (currentLocation.country?.name && currentLocation.name
      ? currentLocation.name
      : undefined);
  const countryName = currentLocation.country?.name;

  if (cityName && countryName) {
    return `${cityName}, ${countryName}`;
  }
  if (cityName) return cityName;
  if (countryName) return countryName;
  if (currentLocation.name) return currentLocation.name;

  return "N/A";
}

export interface CandidateDetail {
  id: number;
  user: number;
  email?: string | null;
  is_profile_public: boolean;
  profile_picture: number | null;
  profile_picture_url?: string | null;
  job_title: string;
  manages_people: boolean;
  full_name: string;
  nationality?: {
    id: number;
    name: string;
  };
  gender: string;
  date_of_birth: string;
  phone_number?: string | null;
  years_of_professional_experience?: {
    id: number;
    name: string;
  };
  awards: string;
  references?: Array<{
    id: number;
    company: {
      id: number;
      name: string;
      profile_picture: string | null;
      website: string | null;
      email: string | null;
      contact_number: string | null;
      industry: string | null;
      country: string | null;
      city: string | null;
      address_line_1: string | null;
      address_line_2: string | null;
      about: string | null;
    };
    name: string;
    email: string;
    phone: string;
  }>;
  current_location?: {
    id: number;
    name?: string;
    city?: CandidateLocationRef | null;
    country?: CandidateLocationRef | null;
  };
  preferred_locations?: Array<{
    id: number;
    country: {
      id: number;
      name: string;
    };
    city: Array<{
      id: number;
      name: string;
    }>;
    employment_type: Array<{
      id: number;
      name: string;
    }>;
    workplace_type: Array<{
      id: number;
      name: string;
    }>;
    visa_status: string;
    driving_license: string;
  }>;
  areas_of_expertise?: Array<{
    id: number;
    field_of_expertise: {
      id: number;
      name: string;
    };
    years_in_this_area_of_expertise: number;
    key_skills: Array<{
      id: number;
      name: string;
    }>;
    industry: Array<{
      id: number;
      name: string;
    }>;
    company: Array<{
      id: number;
      name: string;
      profile_picture: string | null;
      website: string | null;
      email: string | null;
      contact_number: string | null;
      industry: string | null;
      country: string | null;
      city: string | null;
      address_line_1: string | null;
      address_line_2: string | null;
      about: string | null;
    }>;
    region: Array<{
      id: number;
      name: string;
    }>;
    country: Array<{
      id: number;
      name: string;
    }>;
  }>;
  education?: Array<{
    id: number;
    qualification: {
      id: number;
      name: string;
    };
    field_of_study: {
      id: number;
      name: string;
    };
    country: {
      id: number;
      name: string;
    };
    institution: {
      id: number;
      name: string;
    };
    year_of_obtension: number;
    certificate: string | { id: number; name: string } | null;
  }>;
  management_skills?: Array<{
    management_skill: {
      id: number;
      name: string;
    };
    years_of_experience: number;
  }>;
  language_skills?: Array<{
    language: {
      id: number;
      name: string;
    };
    speaking_level: string;
    reading_writing_level: string;
  }>;
  created_at: string;
  updated_at: string;
}

export async function fetchCandidateDetail(
  candidateId: number,
  accessToken: string
): Promise<{ data?: CandidateDetail; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/candidate/candidate/${candidateId}/`;

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
  });

  const responseBody = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: (responseBody && responseBody.message) || 'Failed to fetch candidate details.' };
  }
  const candidateData = responseBody?.data || responseBody;
  return { data: candidateData as CandidateDetail };
}

export type AnonymousFeedbackCandidateDetail = CandidateDetail & {
  profile_picture_url?: string | null;
  phone_number?: string | null;
  match_score?: number | string | null;
};

export async function fetchCandidateByFeedbackToken(
  token: string
): Promise<{ data?: AnonymousFeedbackCandidateDetail; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/candidate/candidate/feedback-token/?token=${encodeURIComponent(token)}`;

  const response = await fetch(endpoint, {
    method: "GET",
    headers: {
      accept: "application/json",
    },
  });

  const responseBody = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: (responseBody && responseBody.message) || "Failed to fetch candidate details." };
  }

  if (responseBody?.error && Object.keys(responseBody.error).length > 0) {
    return { error: responseBody.message || "Failed to fetch candidate details." };
  }

  const candidateData = responseBody?.data || responseBody;
  return { data: candidateData as AnonymousFeedbackCandidateDetail };
}

export interface SubmitAnonymousFeedbackPayload {
  token: string;
  full_name: string;
  comment: string;
  rating: number;
}

export async function submitAnonymousFeedback(
  payload: SubmitAnonymousFeedbackPayload
): Promise<{ data?: any; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/job/feedback/anonymous/submit/`;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const responseBody = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: (responseBody && responseBody.message) || "Failed to submit feedback." };
  }

  if (responseBody?.error && Object.keys(responseBody.error).length > 0) {
    return { error: responseBody.message || "Failed to submit feedback." };
  }

  return { data: responseBody };
}

export interface CreateFeedbackPayload {
  job_post: number;
  candidate: number;
  comment: string;
  rating: number;
}

export async function createFeedback(
  payload: CreateFeedbackPayload,
  accessToken: string
): Promise<{ data?: any; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/job/feedback/create/`;

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "POST",
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(payload),
  });

  const responseBody = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: (responseBody && responseBody.message) || 'Failed to create feedback.' };
  }
  return { data: responseBody };
}

export interface ShareFeedbackPayload {
  job_post: number;
  candidate: number;
  email_ids: string[];
}

export async function shareFeedback(
  payload: ShareFeedbackPayload,
  accessToken: string
): Promise<{ data?: any; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/job/feedback/anonymous/invite/`;

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "POST",
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(payload),
  });

  const responseBody = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: (responseBody && responseBody.message) || 'Failed to share feedback.' };
  }
  return { data: responseBody };
}

export async function toggleFeedbackStatus(
  jobId: number,
  candidateId: number,
  accessToken: string
): Promise<{ data?: any; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/job/feedback/toggle/${jobId}/${candidateId}/`;

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`
    },
  });

  const responseBody = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: (responseBody && responseBody.message) || 'Failed to toggle feedback status.' };
  }
  return { data: responseBody };
}

export async function updateFeedbackStatus(
  jobId: number,
  candidateId: number,
  isEnabled: boolean,
  accessToken: string
): Promise<{ data?: any; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/job/feedback/toggle/${jobId}/${candidateId}/`;

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify({ is_enabled: isEnabled }),
  });

  const responseBody = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: (responseBody && responseBody.message) || 'Failed to update feedback status.' };
  }
  return { data: responseBody };
}

export interface FeedbackItem {
  id: number;
  rating: number;
  candidate: {
    id: number;
    user_id: number;
    profile_picture: number | null;
    profile_picture_url?: string | null;
    is_profile_public: boolean;
    job_title: string | null;
    manages_people: boolean;
    full_name: string;
    nationality: {
      id: number;
      name: string;
    } | null;
    gender: string | null;
    date_of_birth: string | null;
    phone_number?: string | null;
    years_of_professional_experience: {
      id: number;
      name: string;
    } | null;
    awards: string | null;
    current_location: {
      id: number;
      name: string;
    } | null;
  };
  company_member: {
    id: number;
    email: string;
    first_name: string;
    middle_name: string | null;
    last_name: string;
    groups?: string[];
    group?: string;
    designation: string;
    gender: string | null;
    contact_number: string | null;
    company: {
      id: number;
      name: string;
      profile_picture: number | null;
      profile_picture_url?: string | null;
      website: string | null;
      email: string | null;
      contact_number: string | null;
      industry: { id: number; name: string } | string | null;
      country: { id: number; name: string } | string | null;
      city: { id: number; name: string } | string | null;
      region?: { id: number; name: string } | null;
      address_line_1: string | null;
      address_line_2: string | null;
      about: string | null;
    };
    department: {
      id: number;
      name: string;
    } | null;
    profile_picture: number | null;
    profile_picture_url?: string | null;
  } | null;
  full_name: string | null;
  comment: string;
  created_at: string;
  updated_at: string;
}

export interface FeedbackListResponse {
  message: string;
  code: number;
  data: FeedbackItem[];
  error: Record<string, unknown>;
}

export async function fetchFeedbacks(
  jobPostId: number,
  candidateId: number,
  accessToken: string
): Promise<{ data?: FeedbackItem[]; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/job/feedback/list/${jobPostId}/${candidateId}/`;

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "GET",
    headers: {
      accept: "application/json",
      Authorization: `Bearer ${accessToken}`
    },
  });

  const responseBody: FeedbackListResponse = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: (responseBody && responseBody.message) || 'Failed to fetch feedbacks.' };
  }
  return { data: responseBody?.data || [] };
}

export interface AnonymousFeedbackInviteStatus {
  id: number;
  recipient_email: string;
  has_completed_feedback: boolean;
  completed_at: string | null;
  created_at: string;
}

export interface AnonymousFeedbackInviteStatusResponse {
  message: string;
  code: number;
  data: AnonymousFeedbackInviteStatus[];
  error: Record<string, unknown>;
}

export async function fetchAnonymousFeedbackInviteStatus(
  jobPostId: number,
  candidateId: number,
  accessToken: string
): Promise<{ data?: AnonymousFeedbackInviteStatus[]; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/job/feedback/anonymous/invite-status/${jobPostId}/${candidateId}/`;

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "GET",
    headers: {
      accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });

  const responseBody: AnonymousFeedbackInviteStatusResponse = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: (responseBody && responseBody.message) || "Failed to fetch feedback invite statuses." };
  }

  if (responseBody?.error && Object.keys(responseBody.error).length > 0) {
    return { error: responseBody.message || "Failed to fetch feedback invite statuses." };
  }

  return { data: responseBody?.data || [] };
}

export interface SkillsBreakdownResponse {
  message: string;
  code: number;
  data: {
    missing_must_have_skills: Array<{ id: number; name: string }>;
    missing_nice_to_have_skills: Array<{ id: number; name: string }>;
    missing_areas_of_expertise: Array<{ id: number; name: string }>;
    matched_skills_count: number;
    missing_skills_count: number;
  };
  error: Record<string, unknown>;
}

export async function fetchSkillsBreakdown(
  jobId: number,
  matchId: number,
  accessToken: string
): Promise<{ data?: SkillsBreakdownResponse['data']; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/matching/jobs/${jobId}/matches/${matchId}/skills-breakdown/`;

  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
  });

  const responseBody: SkillsBreakdownResponse = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: (responseBody && responseBody.message) || 'Failed to fetch skills breakdown.' };
  }
  return { data: responseBody?.data };
}

